import { computed, ref, useId, watch, type Ref } from 'vue';

interface ListboxOption {
  id: string | number;
  disabled?: boolean;
}

interface ListboxNavigationOptions<T extends ListboxOption> {
  options: () => T[];
  modelValue: () => string | number;
  isDisabled: () => boolean;
  onSelect: (option: T) => void;
  focusTrigger: () => void;
}

export function useListboxNavigation<T extends ListboxOption>(
  config: ListboxNavigationOptions<T>
) {
  const isOpen = ref(false);
  const activeIndex = ref(-1);
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const listId = `${uid}-option`;

  const activeDescendant = computed(() =>
    isOpen.value && activeIndex.value >= 0 ? `${listId}-${activeIndex.value}` : undefined
  );

  const findEnabledIndex = (from: number, step: 1 | -1) => {
    const options = config.options();
    for (let i = from; i >= 0 && i < options.length; i += step) {
      if (!options[i].disabled) return i;
    }
    return -1;
  };

  const openDropdown = () => {
    const options = config.options();
    const selectedIndex = options.findIndex(
      (option) => String(option.id) === String(config.modelValue())
    );
    const selectedUsable = selectedIndex >= 0 && !options[selectedIndex].disabled;
    activeIndex.value = selectedUsable ? selectedIndex : findEnabledIndex(0, 1);
    isOpen.value = true;
  };

  const closeDropdown = (restoreFocus = false) => {
    isOpen.value = false;
    activeIndex.value = -1;
    if (restoreFocus) config.focusTrigger();
  };

  const toggleDropdown = () => {
    if (isOpen.value) closeDropdown();
    else openDropdown();
  };

  const realignActive = () => {
    if (!isOpen.value) return;
    const options = config.options();
    const active = options[activeIndex.value];
    if (active && !active.disabled) return;
    const fallback = findEnabledIndex(Math.min(activeIndex.value, options.length - 1), -1);
    activeIndex.value = fallback >= 0 ? fallback : findEnabledIndex(0, 1);
  };

  const moveActive = (from: number, step: 1 | -1) => {
    const next = findEnabledIndex(from, step);
    if (next >= 0) activeIndex.value = next;
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (config.isDisabled()) return;

    if (!isOpen.value) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        openDropdown();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(activeIndex.value + 1, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(activeIndex.value - 1, -1);
        break;
      case 'Home':
        event.preventDefault();
        moveActive(0, 1);
        break;
      case 'End':
        event.preventDefault();
        moveActive(config.options().length - 1, -1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const active = config.options()[activeIndex.value];
        if (active) config.onSelect(active);
        break;
      }
      case 'Escape':
        // A modal around the select must not also close on this Esc.
        event.preventDefault();
        event.stopPropagation();
        closeDropdown(true);
        break;
      case 'Tab':
        closeDropdown();
        break;
    }
  };

  watch(
    activeIndex,
    (index) => {
      if (index < 0) return;
      document.getElementById(`${listId}-${index}`)?.scrollIntoView?.({ block: 'nearest' });
    },
    { flush: 'post' }
  );

  return {
    isOpen: isOpen as Ref<boolean>,
    activeIndex,
    listboxId,
    listId,
    activeDescendant,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    realignActive,
    handleKeydown,
  };
}
